import AddIcon from '@mui/icons-material/Add';
import { Box, FormControlLabel, Grid, Radio, RadioGroup } from '@mui/material';
import { isEmpty } from 'lodash-es';
import { useEffect, useState } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import {
  IClientCurrencySettings,
  getAllClientCurrencies,
  updateCurrencyStatus,
  updateCurrencyDefault,
} from 'wisestep-api';
import { Label, WsButton, WsDialog, WsTypography, WsTypoVariants } from 'wisestep-base';

import NoDataFoundCard from '../../../components/noDataFoundCard';
import { SettingsPageLayout } from '../../../components/SettingsPageLayout';
import { useWorkspaceContext } from '../../../context';
import {
  getSettingBreadCrumbList,
  ISettingBreadCrumbTypes,
  Pages,
} from '../../../features/teams/helper/defaultValues';
import { AtsCustomAPIAccess, useAtsCustomAPIAccess } from '../../../hooks/useAtsCustomAPIAccess';
import { failed, success, warning } from '../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../utils/NotificationStore';
import { clientSettingsIndexOptions } from '../helper/ClientSettingsHelper';
import { updateStatusQuery } from '../types';

import { HeaderBox, StyledSwitch } from './ClientSettingsLayout';
import CurrencyForm from './CurrencyForm';

const CurrencyDetailsSettings = () => {
  const methods = useForm();
  const { control } = methods;
  const setNotification = useNotificationStore((state) => state.setNotification);
  const [currencies, setCurrencies] = useState<IClientCurrencySettings[]>([]);
  const [openCurrencyList, setOpenCurrencyList] = useState<boolean>(false);
  const [flag, setFlag] = useState<boolean>(false);

  const workspaceContext = useWorkspaceContext();
  const { access: atsCustomAccessControl } = useAtsCustomAPIAccess([
    AtsCustomAPIAccess.MANAGE_CLIENT_SETTINGS,
  ]);

  const manageAccess = workspaceContext.owns || atsCustomAccessControl[0];

  useEffect(() => {
    const fetchData = async () => {
      const response = await getAllClientCurrencies();
      setCurrencies(response.data);
    };
    fetchData();
  }, [flag]);

  const onUpdate = async (data: boolean, index: number) => {
    if (!manageAccess) {
      setNotification(warning('You do not have enough permissions to perform this action'));
      return;
    }
    const temp = currencies[index];
    updateStatusQuery.active = data;
    try {
      if (temp.id) {
        await updateCurrencyStatus(temp.id, updateStatusQuery);
        setNotification(success('Currency has been updated successfully !!'));
        setFlag(!flag);
      } else setNotification(warning('Currency Id not found !!'));
    } catch {
      setNotification(failed('Something went wrong , Please try again !!'));
    }
  };

  const handleAddButton = () => {
    if (!manageAccess) {
      setNotification(warning('You do not have enough permissions to perform this action'));
      return;
    }
    setOpenCurrencyList(true);
  };

  return (
    <>
      <SettingsPageLayout
        title={'Currency Details'}
        indexOptions={clientSettingsIndexOptions}
        breadCrumnbsList={getSettingBreadCrumbList(
          ISettingBreadCrumbTypes.CLIENTS,
          Pages['CURRENCY_DETAILS']
        )}
      >
        <Box component="form" px={8} pt={2}>
          <FormProvider {...methods}>
            <HeaderBox>
              {['Currency', 'Visibility', 'Default'].map((header, index) => (
                <Grid item xs={4} key={index}>
                  <WsTypography variant={WsTypoVariants.SUB_TITLE}>{header}</WsTypography>
                </Grid>
              ))}
            </HeaderBox>
            {isEmpty(currencies) ? (
              <NoDataFoundCard
                text="No Data found, Please click below button to add settings"
                subElement={
                  <Box p={2}>
                    <WsButton startIcon={<AddIcon />} variant="outlined" onClick={handleAddButton}>
                      Add Currency
                    </WsButton>
                  </Box>
                }
              />
            ) : (
              <RadioGroup
                value={currencies.find((c) => c.default)?.id}
                onChange={async (e, v) => {
                  try {
                    if (manageAccess) {
                      await updateCurrencyDefault(v);
                      setFlag(!flag);
                      setNotification(success('Currency has been updated successfully !!'));
                    } else {
                      setNotification(
                        warning('You do not have enough permissions to perform this action')
                      );
                    }
                  } catch {
                    setNotification(failed('Something went wrong , Please try again !!'));
                  }
                }}
              >
                <Grid container pt={6} spacing={2} alignItems="baseline">
                  {currencies?.map((item, index) => (
                    <>
                      <Grid item xs={4}>
                        <Label sx={{ fontWeight: 'bold' }}>{item.symbol}</Label>
                      </Grid>
                      <Grid item xs={4}>
                        <Controller
                          name="value"
                          control={control}
                          render={(props) => (
                            <StyledSwitch
                              defaultChecked={props.field.value ?? item.active}
                              onChange={(_event, checked) => onUpdate(checked, index)}
                            />
                          )}
                        />
                      </Grid>
                      <Grid item xs={4}>
                        <FormControlLabel
                          sx={{ width: '100%' }}
                          key={item.id}
                          value={item.id}
                          label=""
                          control={<Radio />}
                        />
                      </Grid>
                    </>
                  ))}
                </Grid>
              </RadioGroup>
            )}
            <Grid pt={6}>
              {!isEmpty(currencies) && (
                <WsButton startIcon={<AddIcon />} variant="outlined" onClick={handleAddButton}>
                  Add Currency
                </WsButton>
              )}
            </Grid>
          </FormProvider>
        </Box>
      </SettingsPageLayout>

      <WsDialog openDialog={openCurrencyList} size="small">
        <CurrencyForm
          onSave={() => {
            setFlag(!flag);
            setOpenCurrencyList(false);
          }}
          onCancel={() => setOpenCurrencyList(false)}
        />
      </WsDialog>
    </>
  );
};

export default CurrencyDetailsSettings;
